import React, { useMemo, useState } from "react";
import {
  WorkoutRoutine,
  Exercise,
  ExerciseSet,
  User,
  ExerciseReference,
} from "../../api/types.ts";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { Dropdown } from "../widgets/Dropdown.tsx";
import { DropdownItem } from "../widgets/DropdownItem.tsx";
import { InlineTextInput } from "../inputs/InlineTextInput.tsx";
import { RoutineExerciseList } from "./RoutineExerciseList.tsx";
import {
  useCreateWorkout,
  useDeleteWorkoutRoutine,
  usePatchWorkoutRoutine,
} from "../../api/hooks.ts";
import { useDialog } from "../DialogManager.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";
import { Card } from "../containers/Card.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";
import { useToast } from "../ToastContext.tsx";
import { useNavigate } from "react-router-dom";

interface RoutineCardProps {
  routine: WorkoutRoutine;
  exercises: Map<string, Exercise>;
  focusOnMenu?: boolean;
  friends: Partial<User>[] | undefined;
  preferredVisibility?: "friends" | "private";
}

const RoutineCard: React.FC<RoutineCardProps> = ({
  routine,
  exercises,
  focusOnMenu = false,
  friends,
  preferredVisibility,
}) => {
  const [tempRoutineName, setTempRoutineName] = useState(routine.name);

  const patchWorkoutRoutineMutation = usePatchWorkoutRoutine();
  const deleteWorkoutRoutineMutation = useDeleteWorkoutRoutine();
  const createWorkoutMutation = useCreateWorkout();

  const { openDialog, setError } = useDialog();
  const { showInfo } = useToast();
  const navigate = useNavigate();

  const handleDeleteClick = (routineId: string, routineName: string) => {
    openDialog("confirm", {
      onConfirm: () =>
        deleteWorkoutRoutineMutation.mutate(routineId, {
          onError: (e) => {
            setError(e);
          },
        }),
      title: "Delete Routine",
      message: `Are you sure you want to delete "${routineName}"? This action cannot be undone.`,
      infoMessage:
        "Workouts created from this routine are not affected by this action.",
      confirmText: "Delete",
      variant: "danger",
    });
  };

  const handleCreateWorkoutClick = (routine: WorkoutRoutine) => {
    openDialog("confirm", {
      onConfirm: () =>
        createWorkoutMutation.mutate(
          {
            ...routine,
            status: "active",
            visibility: preferredVisibility ?? "private",
            executed_at: new Date().toISOString(),
          },
          {
            onSuccess: () => {
              showInfo(`Created workout "${routine.name}"`);
              navigate("/workouts");
            },
          },
        ),
      title: "Create Workout from Routine",
      message: `Do you want to create a workout from the routine "${routine.name}"? This will create an active workout with the same exercises and sets and default visibility. You may edit it before marking it as completed.`,
      confirmText: "Create",
      variant: "info",
    });
  };

  const availableExercises = useMemo(() => {
    const available = new Map<string, FetchedExercise>();

    exercises.forEach((ex) =>
      available.set(ex.id, { exercise: ex, external: false }),
    );

    return available;
  }, [exercises]);

  const isEditable = routine.exercises.every((ex) =>
    availableExercises.get(ex.exercise_id),
  );

  const handleRoutineNameChange = (newName: string) => {
    setTempRoutineName(newName);
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: {
        ...routine,
        name: newName,
      },
    });
  };

  function moveRoutineExercise(
    routineExercises: ExerciseReference[],
    oldIndex: number,
    newIndex: number,
  ) {
    const removed = routineExercises.splice(oldIndex, 1)[0];
    if (newIndex === 0) {
      routineExercises.unshift(removed);
    } else {
      routineExercises.splice(newIndex, 0, removed);
    }
  }

  const handleRoutineExerciseChange = (
    routineExerciseIndex: number,
    newUnit: string,
    newExerciseId: string,
    newRoutineExerciseIndex?: number,
  ) => {
    const updatedRoutineExercises = routine.exercises.map((ex, idx) => {
      if (idx === routineExerciseIndex) {
        return {
          ...ex,
          exercise_id: newExerciseId,
          resistance_unit: newUnit as "kg" | "lbs",
        };
      }
      return ex;
    });
    if (isNotNullish(newRoutineExerciseIndex)) {
      moveRoutineExercise(
        updatedRoutineExercises,
        routineExerciseIndex,
        newRoutineExerciseIndex,
      );
    }
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { ...routine, exercises: updatedRoutineExercises },
    });
  };

  const handleDeleteRoutineExercise = (routineExerciseIndex: number) => {
    const updatedRoutineExercises = routine.exercises.filter(
      (_, idx) => idx !== routineExerciseIndex,
    );
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { ...routine, exercises: updatedRoutineExercises },
    });
  };

  const handleAddRoutineExercise = (exerciseId: string) => {
    const exercise = exercises.get(exerciseId);
    if (!exercise) return;
    const updatedRoutineExercises = [
      ...routine.exercises,
      {
        exercise_id: exerciseId,
        sets: [] as ExerciseSet[],
        resistance_unit: exercise.default_resistance_unit,
      },
    ];
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { ...routine, exercises: updatedRoutineExercises },
    });
  };

  const handleAddSet = (
    routineExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedRoutineExercises = routine.exercises.map((ex, idx) => {
      if (idx === routineExerciseIndex) {
        return {
          ...ex,
          sets: [...ex.sets, { repetitions, resistance }],
        };
      }
      return ex;
    });
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { ...routine, exercises: updatedRoutineExercises },
    });
  };

  const handleEditSet = (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedRoutineExercises = routine.exercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        // Delete set if both repetitions and resistance are zero
        if (repetitions === 0 && resistance === 0) {
          const updatedSets = ex.sets.filter((_, index) => index !== setIndex);
          return {
            ...ex,
            sets: updatedSets,
          };
        }

        // Otherwise, update the set
        const updatedSets = ex.sets.map((set, index) => {
          if (index === setIndex) {
            return { repetitions, resistance };
          }
          return set;
        });
        return {
          ...ex,
          sets: updatedSets,
        };
      }
      return ex;
    });
    patchWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { ...routine, exercises: updatedRoutineExercises },
    });
  };

  return (
    <>
      <Card>
        <Card.Header>
          <div className="w-full flex flex-row justify-between items-center">
            <div className="w-full" onClick={(e) => e.stopPropagation()}>
              <InlineTextInput
                value={tempRoutineName}
                onChange={handleRoutineNameChange}
                placeholder="Routine name"
                displayAs="h4"
                className="max-w-40 sm:max-w-64"
                displayClassName="font-semibold text-lg"
                disabled={!isEditable}
              />
            </div>
            <div>
              <Dropdown
                trigger={
                  <div
                    className={`flex justify-center ${focusOnMenu ? "border-r-2 rounded-[50%] animate-pulse h-9 w-9 bg-secondary-light display-inline-block" : ""}`}
                  >
                    <button
                      className="p-1 hover:bg-gray-100 rounded-full  transition-colors"
                      aria-label="Routine options"
                    >
                      <HiOutlineDotsVertical
                        className="text-gray-600 animate-none"
                        size={20}
                      />
                    </button>
                  </div>
                }
                alignment="left"
              >
                <DropdownItem
                  onClick={() => handleCreateWorkoutClick(routine)}
                  disabled={!isEditable}
                >
                  Create Workout
                </DropdownItem>
                {friends && friends.length > 0 && (
                  <DropdownItem
                    onClick={() =>
                      openDialog("share-workout-routine", {
                        routine: routine,
                        friends: friends,
                      })
                    }
                    disabled={!isEditable}
                  >
                    Share with Friend
                  </DropdownItem>
                )}
                <DropdownItem
                  onClick={() => handleDeleteClick(routine.id, routine.name)}
                  className="text-red-600 hover:bg-red-50"
                >
                  Delete
                </DropdownItem>
              </Dropdown>
            </div>
          </div>
        </Card.Header>
        <Card.Content>
          <RoutineExerciseList
            resourceId={routine.id}
            routineExercises={routine.exercises}
            availableExercises={availableExercises}
            onExerciseChange={handleRoutineExerciseChange}
            onDeleteExercise={handleDeleteRoutineExercise}
            onAddExercise={handleAddRoutineExercise}
            onAddSet={handleAddSet}
            onEditSet={handleEditSet}
            isEditable={isEditable}
          />
        </Card.Content>
        <Card.Footer>
          <span className="text-sm text-gray-600">
            <span className="font-medium">{routine.exercises.length}</span>{" "}
            exercise
            {routine.exercises.length !== 1 ? "s" : ""}
          </span>
        </Card.Footer>
      </Card>
    </>
  );
};

export { RoutineCard };
